/**
 * SessionStore — Single WebSocket connection for the entire CUI app.
 *
 * Replaces 3+ separate WebSocket connections (App, LayoutManager, CuiLitePanel)
 * with one shared connection. Components subscribe to messages via addMessageHandler.
 *
 * Provides centralized state:
 * - cuiStates: per-account CUI state (idle/processing/done)
 * - sessionStates: per-session attention state (idle/working/needs_attention + reason)
 * - connectionState: WS lifecycle (connecting | online | reconnecting | dead)
 * - serverAlive: derived (true iff connectionState === 'online') — kept for backward compat
 *
 * iOS-Wake behavior: when the tab is backgrounded, iOS kills the WebSocket and
 * throttles setTimeout — the existing reconnect timer may never fire in the
 * suspended tab. We hook visibilitychange/online/pageshow so that the tab
 * returning to foreground forces an immediate reconnect and resets the
 * backoff. Without this the app sits in "all fetches blocked" state until
 * the user manually reloads.
 *
 * Fail-fast: after MAX_RECONNECT_ATTEMPTS consecutive failures we transition
 * to 'dead' and stop the auto-retry loop. The ConnectionBanner surfaces
 * this and offers a manual retry. We deliberately do NOT silently retry
 * forever — silent failure is what this refactor is fixing.
 */

import { nichtZustaendigFallsUnbeantwortet } from '../lib/fensterabfrageAntwort';
import { meldeGemounteteBrowserPanelsBereit } from '../lib/gemounteteBrowserPanels';
import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import type { CuiStates } from '../types';
import { conversationsUrl } from '../utils/conversationScope';

// --- WS lifecycle constants -----------------------------------------------
// Baked into the bundle at build time (vite define). Compared against the
// server's currently-served build id to self-heal stale renderers.
declare const __BUILD_TIME__: string;

const BACKOFF_MIN_MS = 1000;
const BACKOFF_MAX_MS = 10_000;
const BACKOFF_MULTIPLIER = 2;
/** ~5 minutes worth of attempts at max backoff before we give up and ask the user. */
const MAX_RECONNECT_ATTEMPTS = 30;
/**
 * Probe interval AFTER giving up. Giving up is a statement to the USER ("reload
 * me"), not a system-wide kill switch — but that is what it used to be:
 *
 * `window.__cuiServerAlive` is owned here (onopen -> true, onclose -> false) and
 * is read by ~170 call sites across the app, each of which returns early while
 * it is false. When the retry loop stopped, the flag stayed false forever, so
 * every one of those sites stayed disabled until the user reloaded the page —
 * even long after the server was healthy again. CuiLitePanel kept knocking every
 * 10s and being turned away: 546 'connect-deferred-server-dead' events in six
 * days of telemetry, 202 of them in Sahoris sessions. His recurring report
 * "Claude's answers not appearing" is this latch, not the chat panel.
 *
 * So we stay 'dead' in the UI (banner + manual retry button remain), but keep
 * probing far apart. 30s is well above the 10s max backoff, so this is not
 * hammering — it is the difference between "we stopped telling you" and
 * "we stopped trying".
 */
const DEAD_PROBE_MS = 30_000;
/** How long wake() waits for a client-pong before deciding the socket is a zombie (see wake() below). */
const WAKE_PING_TIMEOUT_MS = 3_000;

export type ConnectionState = 'connecting' | 'online' | 'reconnecting' | 'dead';

interface ToolInfo {
  toolName: string;
  toolDetail?: string;
  startedAt: number;
  elapsedMs?: number;
}

interface SessionStateEntry {
  state: 'idle' | 'working' | 'needs_attention';
  reason?: string;
  toolInfo?: ToolInfo;
  /** Timestamp (ms) when this state was last set/confirmed via WS */
  since?: number;
}

interface SessionStoreValue {
  cuiStates: CuiStates;
  sessionStates: Map<string, SessionStateEntry>;
  /** Increments on every sessionStates Map mutation — use as useEffect dependency */
  sessionStatesTick: number;
  /** WS lifecycle state machine. */
  connectionState: ConnectionState;
  /** Derived: connectionState === 'online'. Kept for callers that just want a boolean. */
  serverAlive: boolean;
  /** Number of failed reconnect attempts since the last successful open. */
  reconnectAttempts: number;
  /** Send a JSON message over the shared WebSocket */
  sendWs: (msg: Record<string, unknown>) => void;
  /** Subscribe to raw WS messages. Returns unsubscribe function. */
  addMessageHandler: (handler: (msg: any) => void) => () => void;
  /** Force a connection attempt now — used by the dead-state retry button. */
  forceReconnect: () => void;
}

const SessionStoreContext = createContext<SessionStoreValue | null>(null);

export function useSessionStore(): SessionStoreValue {
  const ctx = useContext(SessionStoreContext);
  if (!ctx) throw new Error('useSessionStore must be used within SessionStoreProvider');
  return ctx;
}

/** Convenience: get cuiStates only */
function useCuiStates(): CuiStates {
  return useSessionStore().cuiStates;
}

/** Convenience: get a single session's state */
function useSessionState(sessionId: string | null): SessionStateEntry | undefined {
  const { sessionStates } = useSessionStore();
  return sessionId ? sessionStates.get(sessionId) : undefined;
}

export function SessionStoreProvider({ children }: { children: ReactNode }) {
  const wsRef = useRef<WebSocket | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [cuiStates, setCuiStates] = useState<CuiStates>({});
  const sessionStatesRef = useRef(new Map<string, SessionStateEntry>());
  // Trigger re-render when sessionStates change (consumers read from ref via context)
  const [sessionStatesTick, setSessionStatesTick] = useState(0);
  const handlersRef = useRef(new Set<(msg: any) => void>());
  // Stable ref so the forceReconnect callback exposed on context survives re-renders.
  const forceReconnectRef = useRef<() => void>(() => {});

  const addMessageHandler = useCallback((handler: (msg: any) => void) => {
    handlersRef.current.add(handler);
    return () => { handlersRef.current.delete(handler); };
  }, []);

  const sendWs = useCallback((msg: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }, []);

  const forceReconnect = useCallback(() => {
    forceReconnectRef.current();
  }, []);

  useEffect(() => {
    let disposed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoff = BACKOFF_MIN_MS;
    let attempts = 0;
    /** Schon aufgegeben? Nur fuer die einmalige Meldung — der Probe-Takt laeuft weiter. */
    let gaveUp = false;

    const clearReconnectTimer = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      attempts += 1;
      setReconnectAttempts(attempts);

      if (attempts > MAX_RECONNECT_ATTEMPTS) {
        // Nur beim Übergang melden — sonst schreibt der Probe-Takt alle 30s ins Log.
        if (!gaveUp) {
          gaveUp = true;
          console.warn(
            '[SessionStore] WS reconnect gave up after', MAX_RECONNECT_ATTEMPTS,
            `attempts — connectionState=dead. Weiter alle ${DEAD_PROBE_MS / 1000}s im Hintergrund,`,
            'damit __cuiServerAlive nicht dauerhaft false bleibt (siehe DEAD_PROBE_MS).',
          );
          setConnectionState('dead');
        }
        // KEIN return ohne neuen Versuch: sonst bleibt die ganze App bis zum
        // manuellen Neuladen tot. onopen setzt attempts/backoff/Flag zurueck
        // und feuert 'cui-reconnected' — die Genesung ist vollstaendig, sie
        // wurde bisher nur nie ausgeloest.
        reconnectTimer = setTimeout(connect, DEAD_PROBE_MS);
        return;
      }

      setConnectionState('reconnecting');
      backoff = Math.min(backoff * BACKOFF_MULTIPLIER, BACKOFF_MAX_MS);
      reconnectTimer = setTimeout(connect, backoff);
    };

    const connect = () => {
      if (disposed) return;
      clearReconnectTimer();

      // Don't stack connections — a wake event can fire while a CONNECTING ws is still pending.
      const existing = wsRef.current;
      if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
        return;
      }

      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${protocol}://${window.location.host}/ws`);
      wsRef.current = ws;
      // onerror is intentionally a no-op: onclose always fires after onerror and is the
      // single source of truth for "reconnect now". Double-handling causes double-retry.
      ws.onerror = () => {};

      ws.onopen = () => {
        if (disposed) { ws.close(); return; }
        const wasDown = window.__cuiServerAlive === false;
        window.__cuiServerAlive = true;
        backoff = BACKOFF_MIN_MS;
        attempts = 0;
        gaveUp = false;
        setReconnectAttempts(0);
        setConnectionState('online');
        console.log('[SessionStore] WS connected');
        if (wasDown) window.dispatchEvent(new CustomEvent('cui-reconnected'));

        // Headless shadow client (?shadow=1, cui-shadow-client.service):
        // identify on EVERY (re)connect, directly in onopen. AppContent's
        // client:hello handler mounts only after the auth gate resolves — on
        // auth-enabled servers (partner) the hello has usually already passed
        // by then and the registration was silently lost.
        if (new URLSearchParams(window.location.search).get('shadow') === '1') {
          ws.send(JSON.stringify({ type: 'client:register', shadow: true }));
          console.log('[SessionStore] Shadow registration sent');
        }

        // Pre-populate session states from server on connect
        fetch(conversationsUrl('/api/mission/conversations'), { signal: AbortSignal.timeout(5000) })
          .then(r => r.json())
          .then(data => {
            const convs = Array.isArray(data) ? data : data?.conversations || [];
            let changed = false;
            for (const c of convs) {
              if (!c.sessionId || c.status !== 'ongoing') continue;
              if (!sessionStatesRef.current.has(c.sessionId)) {
                const state = c.attentionState || 'idle';
                const reason = c.attentionReason;
                sessionStatesRef.current.set(c.sessionId, { state: state as any, reason, since: Date.now() });
                changed = true;
              }
            }
            if (changed) setSessionStatesTick(t => t + 1);
          })
          .catch(() => {}); // silent-ok: initial session state fetch is non-critical; WS events populate state
      };

      ws.onclose = () => {
        window.__cuiServerAlive = false;
        // Only clear our ref if it still points at this ws — a wake-triggered
        // reconnect may have already replaced it.
        if (wsRef.current === ws) wsRef.current = null;
        if (disposed) return;
        if (attempts === 0) console.log('[SessionStore] WS disconnected, reconnecting...');
        scheduleReconnect();
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);

          // Stable per-connection clientId from the server. Stash centrally —
          // AppContent's hello handler mounts only after the auth gate and can
          // miss the hello entirely (auth-enabled servers). BrowserPanel echoes
          // this in /browser-panel/ready so the server routes eval/click/
          // screenshot to the owning frontend instead of broadcasting.
          if (msg.type === 'client:hello' && typeof msg.clientId === 'string') {
            (window as unknown as { __CUI_CLIENT_ID__?: string }).__CUI_CLIENT_ID__ = msg.clientId;
            // Der Server verliert sein fluechtiges Panel-Besitzerregister bei
            // jedem Neustart. Die sichtbaren BrowserPanels bleiben im Desktop-
            // Fenster aber gemountet und ihr Mount-Effekt laeuft nicht erneut.
            // Deshalb die lokale, echte Panelmenge unter der neuen Verbindung
            // erneut anmelden; sonst endet /eval trotz sichtbarer Buehnenkachel
            // mit 404 "Unknown browser panel".
            void meldeGemounteteBrowserPanelsBereit(msg.clientId).catch((error) => {
              console.error('[SessionStore] Browserpanels nach Verbindung nicht angemeldet', error);
            });
            // Stale-bundle self-heal: if the server's served build id differs
            // from the one baked into THIS running bundle, we are running old JS
            // and must reload. Handled here (not just AppContent) because this
            // WS handler is always active, incl. the headless shadow client and
            // before the auth gate — the shadow stuck on a pre-fix bundle was
            // the root cause of the 2026-07-23 panel sprawl. Loop-guarded:
            // reload at most once per target build id so a persistent
            // server/dist mismatch can never spin the renderer.
            if (typeof msg.buildId === 'string' && msg.buildId && msg.buildId !== __BUILD_TIME__) {
              const KEY = '__cui_reloaded_for_build__';
              try {
                if (sessionStorage.getItem(KEY) !== msg.buildId) {
                  sessionStorage.setItem(KEY, msg.buildId);
                  console.warn(`[SessionStore] stale bundle (running ${__BUILD_TIME__}, served ${msg.buildId}) — reloading`);
                  window.location.reload();
                  return;
                }
                console.warn(`[SessionStore] stale bundle persists (served ${msg.buildId}) — already reloaded once, not looping`);
              } catch {
                console.warn('[SessionStore] stale bundle, sessionStorage unavailable — reloading');
                window.location.reload();
                return;
              }
            }
          }

          // Centralized state tracking
          if (msg.type === 'cui-state' && msg.cuiId && msg.state) {
            setCuiStates(prev => prev[msg.cuiId] === msg.state ? prev : { ...prev, [msg.cuiId]: msg.state });
            // Also track sessionStates from cui-state
            if (msg.sessionId) {
              const mapped = msg.state === 'processing' ? 'working' : msg.state === 'done' ? 'idle' : msg.state;
              const prev = sessionStatesRef.current.get(msg.sessionId);
              if (!prev || prev.state !== mapped) {
                sessionStatesRef.current.set(msg.sessionId, { state: mapped as any, reason: prev?.reason, since: Date.now() });
                setSessionStatesTick(t => t + 1);
              }
            }
          }

          if (msg.type === 'conv-attention' && msg.sessionId) {
            const prev = sessionStatesRef.current.get(msg.sessionId);
            const newState = msg.state || 'idle';
            const newReason = msg.reason;
            const newToolInfo = msg.toolInfo || undefined;
            if (!prev || prev.state !== newState || prev.reason !== newReason || prev.toolInfo?.toolName !== newToolInfo?.toolName) {
              sessionStatesRef.current.set(msg.sessionId, { state: newState, reason: newReason, toolInfo: newToolInfo, since: Date.now() });
              setSessionStatesTick(t => t + 1);
            }
          }

          // Tool execution tracking
          if (msg.type === 'tool-executing' && msg.sessionId) {
            const prev = sessionStatesRef.current.get(msg.sessionId);
            sessionStatesRef.current.set(msg.sessionId, {
              state: prev?.state || 'working',
              reason: prev?.reason,
              toolInfo: { toolName: msg.toolName, toolDetail: msg.toolDetail, startedAt: msg.startedAt },
              since: Date.now(),
            });
            setSessionStatesTick(t => t + 1);
          }
          if (msg.type === 'tool-done' && msg.sessionId) {
            const prev = sessionStatesRef.current.get(msg.sessionId);
            if (prev?.toolInfo) {
              sessionStatesRef.current.set(msg.sessionId, { state: prev.state, reason: prev.reason, since: Date.now() });
              setSessionStatesTick(t => t + 1);
            }
          }
          if (msg.type === 'tool-heartbeat' && msg.sessionId) {
            const prev = sessionStatesRef.current.get(msg.sessionId);
            if (prev) {
              sessionStatesRef.current.set(msg.sessionId, {
                ...prev,
                toolInfo: { toolName: msg.toolName, toolDetail: msg.toolDetail, startedAt: prev.toolInfo?.startedAt || Date.now(), elapsedMs: msg.elapsedMs },
                since: Date.now(),
              });
              setSessionStatesTick(t => t + 1);
            }
          }

          // Bulk session state init (sent on WS connect for state recovery)
          if (msg.type === "session-states-init" && msg.states) {
            const states = msg.states as Record<string, { state: string; reason?: string; sessionId?: string }>;
            let changed = false;
            for (const [key, val] of Object.entries(states)) {
              const sid = val.sessionId || key;
              const prev = sessionStatesRef.current.get(sid);
              if (!prev || prev.state !== val.state || prev.reason !== val.reason) {
                sessionStatesRef.current.set(sid, { state: val.state as any, reason: val.reason as any, since: Date.now() });
                changed = true;
              }
            }
            if (changed) setSessionStatesTick(t => t + 1);
          }

          // Dispatch to all subscribers
          for (const handler of handlersRef.current) {
            try { handler(msg); } catch (err) {
              console.warn('[SessionStore] Handler error:', err);
            }
          }
          // Fensterabfrage, die keine Oberflaeche dieser Seite beantwortet hat: „nicht zustaendig"
          // statt Schweigen (lib/fensterabfrageAntwort.ts) — die App beantwortet synchron oben.
          if (msg.type === 'control:guide-fenster') {
            nichtZustaendigFallsUnbeantwortet(msg.abfrageId, (window as unknown as { __CUI_CLIENT_ID__?: string }).__CUI_CLIENT_ID__);
          }
        } catch (err) {
          console.warn('[SessionStore] WS parse error:', err);
        }
      };
    };

    /**
     * Abandon a (suspected-zombie) socket and reconnect immediately, resetting
     * backoff — the same recovery an eventual onclose triggers, just forced
     * proactively instead of waiting for the OS to notice the TCP connection
     * is actually dead (which, for a half-open connection, can take minutes —
     * see wake() below).
     */
    const forceReplaceConnection = (ws: WebSocket) => {
      if (disposed) return;
      if (wsRef.current === ws) wsRef.current = null;
      try { ws.close(); } catch { /* already gone */ }
      backoff = BACKOFF_MIN_MS;
      attempts = 0;
      setReconnectAttempts(0);
      clearReconnectTimer();
      connect();
    };

    /**
     * Wake-trigger: tab became visible, network came back, or BFCache restore.
     * On iOS, background tabs are suspended — setTimeout backoffs may never fire
     * in the background, so the existing reconnect schedule is effectively dead.
     * We reset the backoff and force an attempt now. Wake from a 'dead' state
     * counts as user intent → reset the attempt counter.
     *
     * `readyState === OPEN` is NOT sufficient evidence the connection still
     * works: after the machine sleeps or switches networks, the OS-level TCP
     * connection can go half-open while the browser's WebSocket object keeps
     * reporting OPEN indefinitely — there is no client-triggerable ping in the
     * WebSocket API, so nothing detects this until an actual send/receive
     * fails. Trusting readyState here used to skip reconnecting a truly-dead
     * socket on every wake event: the app then looked alive (fire-and-forget
     * commands like browser-panel/navigate report "ok", since handing a
     * message to a half-open socket doesn't throw) while anything that awaits
     * a round-trip ack (eval/click/type/screenshot) silently timed out — the
     * exact "navigate said ok but eval/screenshot timed out after the Mac
     * woke from sleep" symptom from the 2026-08-16 incident. Verify with an
     * application-level ping/pong (server echoes client-ping → client-pong,
     * see state.ts) instead of trusting the flag.
     */
    const wake = (reason: string) => {
      if (disposed) return;
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        console.log('[SessionStore] Wake-triggered reconnect:', reason);
        backoff = BACKOFF_MIN_MS;
        attempts = 0;
        setReconnectAttempts(0);
        clearReconnectTimer();
        connect();
        return;
      }
      console.log('[SessionStore] Wake-triggered liveness check:', reason);
      const probeId = `wake-${reason}-${Math.floor(Math.random() * 1e9)}`;
      let answered = false;
      const onProbeMessage = (e: MessageEvent) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg?.type === 'client-pong' && msg.id === probeId) answered = true;
        } catch { /* not JSON / not ours — ignore */ }
      };
      ws.addEventListener('message', onProbeMessage);
      try {
        ws.send(JSON.stringify({ type: 'client-ping', id: probeId }));
      } catch {
        ws.removeEventListener('message', onProbeMessage);
        forceReplaceConnection(ws);
        return;
      }
      setTimeout(() => {
        ws.removeEventListener('message', onProbeMessage);
        if (answered || disposed) return;
        console.warn('[SessionStore] Wake liveness check timed out — socket reports OPEN but never answered, forcing reconnect');
        forceReplaceConnection(ws);
      }, WAKE_PING_TIMEOUT_MS);
    };

    forceReconnectRef.current = () => wake('manual-retry');

    const onVisibility = () => {
      if (document.visibilityState === 'visible') wake('visibilitychange');
    };
    const onOnline = () => wake('online-event');
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) wake('pageshow-bfcache');
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('pageshow', onPageShow);

    connect();

    return () => {
      disposed = true;
      clearReconnectTimer();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pageshow', onPageShow);
      wsRef.current?.close();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Context value — changes when cuiStates, sessionStatesTick, connectionState or reconnectAttempts change
  const value: SessionStoreValue = {
    cuiStates,
    sessionStates: sessionStatesRef.current,
    sessionStatesTick,
    connectionState,
    serverAlive: connectionState === 'online',
    reconnectAttempts,
    sendWs,
    addMessageHandler,
    forceReconnect,
  };

  return (
    <SessionStoreContext.Provider value={value}>
      {children}
    </SessionStoreContext.Provider>
  );
}
